import React, { useState } from "react";
import { Plus, Minus } from "lucide-react";

/**
 * Accessible FAQ accordion + auto-injects FAQPage JSON-LD into the
 * document head (via a sibling JsonLd component the parent renders).
 *
 * Props:
 *   - items: [{ q: string, a: string | ReactNode }, ...]
 *   - title: optional section heading
 *   - testId: optional data-testid prefix
 */
export default function FaqSection({ items = [], title = "Frequently asked questions", testId = "faq" }) {
  const [open, setOpen] = useState(0);
  if (!items.length) return null;

  return (
    <section className="border-t border-[#2a2620]" data-testid={testId}>
      <div className="max-w-4xl mx-auto px-6 sm:px-8 py-16">
        <h2 className="font-serif-display text-3xl sm:text-4xl text-[#f4efe6]">{title}</h2>
        <div className="mt-8 divide-y divide-[#2a2620] border-y border-[#2a2620]">
          {items.map((item, i) => {
            const isOpen = open === i;
            return (
              <div key={i} data-testid={`${testId}-item-${i}`}>
                <button
                  type="button"
                  onClick={() => setOpen(isOpen ? -1 : i)}
                  className="w-full flex items-center justify-between gap-4 py-5 text-left group"
                  aria-expanded={isOpen}
                >
                  <span className="font-serif-display text-lg text-[#f4efe6] group-hover:text-[#b8963e] transition-colors">
                    {item.q}
                  </span>
                  <span className="shrink-0 text-[#b8963e]">
                    {isOpen ? <Minus className="w-5 h-5" /> : <Plus className="w-5 h-5" />}
                  </span>
                </button>
                {isOpen && (
                  <div className="pb-5 text-[#c9bca4] leading-relaxed">
                    {typeof item.a === "string" ? <p>{item.a}</p> : item.a}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}


/**
 * FAQPage JSON-LD generator. Render alongside FaqSection inside a Helmet block.
 *   <Helmet><script type="application/ld+json">{JSON.stringify(faqJsonLd(faqs))}</script></Helmet>
 */
export function faqJsonLd(items) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": items.map((it) => ({
      "@type": "Question",
      "name": it.q,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": typeof it.a === "string" ? it.a : (it.aPlain || ""),
      },
    })),
  };
}
